import { loadRevisionDataThunk } from "@/data-preparation-tool/loading/load-revision-data-thunk";
import { useErrorHandlers } from "@/errors/components/error-handling-context";
import { projectUnsupportedError } from "@/errors/unrecoverable-error";
import { addBoundingBoxes } from "@/features/ai-chat/ai-slice";
import { setActiveCad } from "@/store/cad/cad-slice";
import { setProjectIntegrations } from "@/store/integrations/integrations-slice";
import { isLightweightDataSetCapture } from "@/store/lightweight-elements-discriminators";
import {
  // In this source file, all lightweight elements are fetched during the project initial load
  // eslint-disable-next-line no-restricted-imports
  addLightweightElements,
  LightweightElement,
} from "@/store/lightweight-elements-slice";
import { setRevisions } from "@/store/revisions-slice";
import { setActiveArea, setActiveElement } from "@/store/selections-slice";
import { AppDispatch } from "@/store/store";
import { useAppDispatch } from "@/store/store-hooks";
import { addNewSystemTags, addNewTags } from "@/store/tags/tags-slice";
import { assert, GUID } from "@faro-lotv/foundation";
import {
  IElement,
  IElementGenericDataset,
  IElementType,
  IElementTypeHint,
  isIElementAreaSection,
  isIElementGenericDataset,
  isIElementProjectRoot,
  validateIElement,
} from "@faro-lotv/ielement-types";
import {
  addAreaDataSets,
  addClusters,
  // In this source file, all initial iElements are fetched during the project initial load
  // eslint-disable-next-line no-restricted-imports
  addIElements,
  fetchProjectIElements,
  initializeProject,
  setRootId,
} from "@faro-lotv/project-source";
import {
  DataSetAreaInfo,
  isProjectNotFoundError,
  ProjectApi,
  ProjectApiError,
  useApiClientContext,
} from "@faro-lotv/service-wires";
import { chunk } from "es-toolkit";
import { useEffect, useState } from "react";
import { fetchBimModelGroupAtRoot } from "../point-cloud-file-upload-context/cad-upload-utils";
import { IElementFetchRequests } from "./project-loading-context";
import { useProjectLoadingStore } from "./project-loading-store";

/** Safe number of GUIDs that can be included in an iElements request, before the URL gets too long. */
export const IELEMENTS_REQUEST_CHUNK_SIZE = 50;

/**
 * The datasets an area contains, as resolved by the volume queries.
 *
 * Those queries resolve independently from the iElement requests, so the mapping must only be
 * dispatched once the area itself is in the store: the world transform of a dataset is computed
 * relative to its area.
 */
type AreaDataSets = {
  /** Id of the area the datasets are aligned to */
  areaId: GUID;

  /** The datasets contained in the area */
  dataSets: DataSetAreaInfo[];
};

/**
 * @param projectId of the project to prepare
 * @param requiredItem inside the project we need the sub-tree of
 * @returns true when the minimum required data from the project was loaded from the backend
 */
export function useProjectInitialData(
  projectId: GUID,
  requiredItem?: GUID | null,
): boolean {
  const dispatch = useAppDispatch();
  const { handleErrorWithPage } = useErrorHandlers();
  const { mustLoadInitialData, initialDataLoaded } = useProjectLoadingStore();

  const [isProjectReady, setIsProjectReady] = useState(false);

  const { projectApiClient: client } = useApiClientContext();

  // Load all initial data required to work on a project
  // * The project tree up to the list of area sections
  // * All the extra data defined in the fetchInitExtraData function
  useEffect(() => {
    if (!mustLoadInitialData(projectId)) {
      setIsProjectReady(true);
      return;
    }
    dispatch(initializeProject(projectId));

    const ac = new AbortController();

    // Load revision data in the background
    // Used e.g. for change indicators in the Live/Draft toggle
    dispatch(
      loadRevisionDataThunk({ projectApiClient: client, signal: ac.signal }),
    ).catch(() => {
      // Non critical data, errors can be ignored to avoid disrupting the user
    });

    // Retry fetchProject if it fails
    fetchProjectInitialData(dispatch, client, ac.signal)
      .then((elements: IElement[]) => {
        // The getAllIElements request returns an empty array if the request is aborted
        if (ac.signal.aborted) {
          return;
        }
        const areaSections = elements.filter(
          (element) =>
            element.typeHint === IElementTypeHint.area &&
            element.type === IElementType.section,
        );
        const isProjectWithArea = areaSections.length > 0;

        if (isProjectWithArea) {
          dispatch(setActiveElement(areaSections[0].id));
          dispatch(setActiveArea(areaSections[0].id));
        } else {
          const slideContainers = elements.filter(
            (element) =>
              element.typeHint === IElementTypeHint.slideContainer &&
              element.type === IElementType.group,
          );
          if (slideContainers.length > 0) {
            dispatch(setActiveElement(slideContainers[0].id));
          }
        }

        const cadModels = elements.filter(
          (element) => element.type === IElementType.model3dStream,
        );

        if (cadModels.length) {
          // sort models in alphabetical order of names
          const sortedCadModels = cadModels.sort((a, b) =>
            a.name.localeCompare(b.name),
          );
          dispatch(setActiveCad(sortedCadModels[0].id));
        }

        return fetchInitExtraData(
          dispatch,
          client,
          ac.signal,
          requiredItem ?? undefined,
        ).then(() => {
          // The getAllIElements request returns an empty array if the request is aborted
          if (ac.signal.aborted) {
            return;
          }

          setIsProjectReady(true);
          initialDataLoaded(projectId);
        });
      })
      .catch((e: ProjectApiError) => {
        // In case the request was aborted due to the hook unmount, don't show the error page to avoid race conditions
        if (ac.signal.aborted) {
          return;
        }

        if (isProjectNotFoundError(e)) {
          handleErrorWithPage(projectUnsupportedError(e, projectId));
          return;
        }
        handleErrorWithPage(e);
      });

    return () => {
      ac.abort();
      setIsProjectReady(false);
    };
  }, [
    client,
    dispatch,
    handleErrorWithPage,
    projectId,
    requiredItem,
    initialDataLoaded,
    mustLoadInitialData,
  ]);

  return isProjectReady;
}

/**
 * Update the store with the elements fetched from the ProjectAPI
 *
 * @param elementCollection the list of elements fetched from the ProjectAPI
 * @param dispatch function to update the application store
 * @returns the list of elements
 */
function updateStoreWithElements(
  elementCollection: IElement[],
  dispatch: AppDispatch,
): IElement[] {
  // Build the initial tree removing the duplicated nodes
  const elements = elementCollection.reduce<IElement[]>((result, next) => {
    if (!result.some((el) => el.id === next.id)) {
      result.push(next);
    }
    return result;
  }, []);
  const root = elements.find((el) => el.id === elements[0].rootId);

  dispatch(addIElements(elements));
  if (root) {
    const projectIntegrations = isIElementProjectRoot(root)
      ? (root.metaDataMap?.projectIntegrations ?? undefined)
      : undefined;
    dispatch(setProjectIntegrations(projectIntegrations));
    dispatch(setRootId(root.id));
  }
  return elements;
}

/**
 * Fetch the initial data needed for the viewer to render a project
 *
 * For now we require all the nodes up the the area section so we can open the Sheet Mode
 *
 * @param dispatch function to update the application store
 * @param client to talk to the project api endpoint
 * @param signal to abort this fetch
 * @returns the list of element returned by the backend or an empty array if the request was aborted
 */
export async function fetchProjectInitialData(
  dispatch: AppDispatch,
  client: ProjectApi,
  signal: AbortSignal,
): Promise<IElement[]> {
  // Fetch all the area sections
  const areaSections = await client.getAllIElements({
    signal,
    types: [IElementType.section],
    typeHints: [IElementTypeHint.area],
  });
  if (signal.aborted) return [];

  const [
    elements,
    labels,
    clusters,
    revisions,
    lightweightIElements,
    aiBoundingBoxes,
  ] = await Promise.all([
    // elements
    Promise.all([
      fetchAreasAndSheets(areaSections, client, signal),
      fetchCads(client, signal),
      fetchMarkupTemplates(client, signal),
      fetchCaptureTreeMainStructure(client, signal),
      fetchUnassigned2dPhotos(client, signal),
    ]),
    // labels
    client.getProjectLabels(signal),
    // clusters
    client.getCaptureTree(signal),
    // revisions
    client.getAllRegistrationRevisions(signal),
    // lightweightIElements
    fetchLightweightStructure(client, signal),
    // AI bounding boxes
    client.boundingBoxes(),
  ]);

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (signal.aborted) return [];

  dispatch(addNewTags(labels.allowedToUse));
  dispatch(addNewSystemTags(labels.system));
  dispatch(addClusters(clusters));
  dispatch(setRevisions(revisions));
  if (lightweightIElements.elements.length) {
    dispatch(addLightweightElements(lightweightIElements.elements));
  }
  if (aiBoundingBoxes.length) {
    dispatch(addBoundingBoxes(aiBoundingBoxes));
  }

  const storedElements = updateStoreWithElements(elements.flat(), dispatch);

  // The areas are part of the batch above, so their world transforms are known by now
  for (const areaDataSets of lightweightIElements.areaDataSets) {
    dispatch(addAreaDataSets(areaDataSets));
  }

  return storedElements;
}

/**
 * @returns the project branches for all the areas up to all the img-sheets tiled or not
 * @param areaSections of all the project areas
 * @param client to query the elements
 * @param signal to abort the request
 */
async function fetchAreasAndSheets(
  areaSections: IElement[],
  client: ProjectApi,
  signal: AbortSignal,
): Promise<IElement[]> {
  const areaIds = areaSections.map((el) => el.id);
  const areaIdChunks = chunk(areaIds, IELEMENTS_REQUEST_CHUNK_SIZE);

  const chunkedImgSheetsRequests: IElementFetchRequests = [];
  const chunkedImgSheetTiledRequests: IElementFetchRequests = [];

  // Get all the sheets in all the areas of the project, subdividing by chunks
  // to reduce the request size
  for (const areaIdChunk of areaIdChunks) {
    // Get all the imgSheets of this chunk
    chunkedImgSheetsRequests.push(
      client.getAllIElements({
        signal,
        ancestorIds: areaIdChunk,
        types: [IElementType.imgSheet],
      }),
    );

    chunkedImgSheetTiledRequests.push(
      // Get all the imgSheetTiled of this chunk
      client.getAllIElements({
        signal,
        ancestorIds: areaIdChunk,
        types: [IElementType.imgSheetTiled],
      }),
    );
  }

  // Get all ancestors of the areas (from the area to the Root)
  const areasAncestors = client.getAllIElements({
    signal,
    descendantIds: [areaIds[0]],
  });

  // Get all the Group-Areas so that the tree from the root up to the sheet is loaded locally
  const areaGroupRequest = client.getAllIElements({
    signal,
    types: [IElementType.group],
    typeHints: [IElementTypeHint.area],
  });

  const sheetResponses = await Promise.all([
    Promise.all(chunkedImgSheetsRequests),
    Promise.all(chunkedImgSheetTiledRequests),
  ]);
  const sheets = sheetResponses[0];
  const tiledSheets = sheetResponses[1];

  // Among all the sheets in an area, there could be sheets that are not inside a Group-Area:
  // for example this happens for Geoslam data. We need to load the dataset though, so that
  // we can pick the right sheet when the user selects the area containing this dataset, instead
  // of picking the default grid. To do so, we need to load all the ancestors of the sheets whose
  // parent is not an area group
  const groups = await areaGroupRequest;
  const missingSheetIds: GUID[] = [];
  for (const sheet of [...sheets.flat(), ...tiledSheets.flat()]) {
    if (!groups.find((g) => g.id === sheet.parentId)) {
      missingSheetIds.push(sheet.id);
    }
  }
  const sheetIdChunks = chunk(missingSheetIds, IELEMENTS_REQUEST_CHUNK_SIZE);
  const chunkedMissingSheetsRequests: IElementFetchRequests = [];
  for (const sheetChunk of sheetIdChunks) {
    chunkedMissingSheetsRequests.push(
      // Get all the imgSheetTiled of this chunk
      client.getAllIElements({
        signal,
        descendantIds: sheetChunk,
      }),
    );
  }

  const missingSheets = await Promise.all(chunkedMissingSheetsRequests);
  const ancestors = await areasAncestors;

  return [
    ...areaSections,
    ...ancestors,
    ...groups,
    ...sheets.flat(),
    ...tiledSheets.flat(),
    ...missingSheets.flat(),
  ];
}

/**
 * @returns all the cad IElements in the project
 * @param client to query the project
 * @param signal to abort the request
 */
function fetchCads(
  client: ProjectApi,
  signal: AbortSignal,
): Promise<IElement[]> {
  return fetchBimModelGroupAtRoot(client, signal).then((cadGroup) =>
    cadGroup ? client.getAllIElements({ ancestorIds: [cadGroup.id] }) : [],
  );
}

/**
 * @returns all the markup templates for the project
 * @param client to query the project
 * @param signal to abort the request
 */
async function fetchMarkupTemplates(
  client: ProjectApi,
  signal: AbortSignal,
): Promise<IElement[]> {
  const markupTemplatesGroup = await client.getAllIElements({
    signal,
    types: [IElementType.group],
    typeHints: [IElementTypeHint.markup],
  });
  if (markupTemplatesGroup.length === 0) return [];

  return client.getAllIElements({
    signal,
    ancestorIds: markupTemplatesGroup.map((group) => group.id),
  });
}

/**
 * @returns all the unassigned 2D photos for the project.
 * @param client - The ProjectApi client used to fetch elements
 * @param signal - AbortSignal to cancel the fetch operation if needed
 * @throws {Error} Throws an assertion error if more than one unassigned 2D photos group is found
 */
async function fetchUnassigned2dPhotos(
  client: ProjectApi,
  signal: AbortSignal,
): Promise<IElement[]> {
  const unassigned2dPhotosGroup = await client.getAllIElements({
    signal,
    types: [IElementType.group],
    typeHints: [IElementTypeHint.unplacedUploads],
  });
  if (unassigned2dPhotosGroup.length === 0) return [];
  assert(
    unassigned2dPhotosGroup.length === 1,
    "More than one unassigned 2d photos group found",
  );
  return client.getAllIElements({
    signal,
    ancestorIds: [unassigned2dPhotosGroup[0].id],
  });
}

/**
 * Fetch all the extra data needed at app init for a specific area section
 *
 * @param dispatch function to update the application store
 * @param client to talk to the project api endpoint
 * @param signal to abort this fetch
 * @param requiredItem GUID of an element we need to have the entire sub-tree in the project at the start
 */
async function fetchInitExtraData(
  dispatch: AppDispatch,
  client: ProjectApi,
  signal: AbortSignal,
  requiredItem?: GUID,
): Promise<void> {
  // Fetch all the laserScan sections as we need them for UI validation when the viewer starts
  await dispatch(
    fetchProjectIElements({
      fetcher: () =>
        client.getAllIElements({
          signal,
          types: [IElementType.section],
          typeHints: [IElementTypeHint.dataSession],
        }),
    }),
  );

  // If we need a specific element, fetch the sub-tree containing that element
  if (!requiredItem) return;
  const response = await dispatch(
    fetchProjectIElements({
      fetcher: () =>
        client.getAllIElements({
          signal,
          descendantIds: [requiredItem],
        }),
    }),
  );

  const { payload } = response;
  if (!Array.isArray(payload)) {
    return;
  }

  const loadedSubtree = payload.filter(validateIElement);

  // If the ancestors list contains an area, load all the descendants of that area
  let areaIdToFetch = loadedSubtree.find(isIElementAreaSection)?.id;

  // If the element is in the capture tree, we need to load an area that contains it, as well as the data set
  const captureTreeDataSet = loadedSubtree.find(isIElementGenericDataset);
  let dataSetToFetch: GUID | undefined;
  let areaDataSets: AreaDataSets | undefined;

  if (!areaIdToFetch && captureTreeDataSet) {
    // There is no race condition here, areaIdToFetch does not change by the call to fetchAreaForCaptureTreeDataSet.
    areaDataSets = await fetchAreaForCaptureTreeDataSet(
      captureTreeDataSet,
      client,
      signal,
    );
    areaIdToFetch = areaDataSets?.areaId;

    // Loading the data set makes sure that split-screen links can be validated on load
    dataSetToFetch = captureTreeDataSet.id;
  }

  const ancestorIds = [
    ...(areaIdToFetch ? [areaIdToFetch] : []),
    ...(dataSetToFetch ? [dataSetToFetch] : []),
  ];

  if (!ancestorIds.length) {
    return;
  }

  await dispatch(
    fetchProjectIElements({
      fetcher: () =>
        client.getAllIElements({
          signal,
          ancestorIds,
        }),
    }),
  );

  if (areaDataSets) {
    dispatch(addAreaDataSets(areaDataSets));
  }
}

/**
 * @returns The first area a capture tree data set is aligned to, and the datasets it contains.
 *  The caller is responsible for dispatching the mapping.
 * @param captureTreeDataSet the data set to look up the area for
 * @param client project api client to use
 * @param signal abort signal for network requests
 */
async function fetchAreaForCaptureTreeDataSet(
  captureTreeDataSet: IElementGenericDataset,
  client: ProjectApi,
  signal: AbortSignal,
): Promise<AreaDataSets | undefined> {
  const areas = await client.queryAreaVolumeInverse(
    captureTreeDataSet.id,
    signal,
  );

  // Use the first fitting area to show a deep-linked element in
  const areaId = areas[0]?.elementId;

  if (!areaId) {
    return;
  }

  // Also make sure the store contains the alignment edges of the area, so further logic can use them
  const dataSets = await client.queryAreaVolume(areaId, signal);

  return { areaId, dataSets };
}

/**
 * Load the main structure of the capture tree as it will be needed to link
 * the datasets to the rest of the project
 *
 * The datasets will be loaded incrementally each time an area is loaded
 *
 * @returns the capture tree main structure without the single datasets
 * @param client to query data
 * @param signal to abort the request
 */
async function fetchCaptureTreeMainStructure(
  client: ProjectApi,
  signal: AbortSignal,
): Promise<IElement[]> {
  const captureTreeSectionRequest = await client.getAllIElements({
    signal,
    type: IElementType.section,
    typeHints: [IElementTypeHint.captureTree],
  });
  const captureTreeRoot = captureTreeSectionRequest.at(0);

  if (!captureTreeRoot?.childrenIds) {
    return [];
  }

  const captureTreeChildren = await client.getAllIElements({
    signal,
    ids: captureTreeRoot.childrenIds,
  });
  return [captureTreeRoot, ...captureTreeChildren];
}

/**
 * @returns The flat list of elements in the Lightweight structure, and the areas their datasets
 *  are aligned to. The caller is responsible for dispatching the mapping.
 * @param client The project API client
 * @param signal Object to abort the requests
 */
async function fetchLightweightStructure(
  client: ProjectApi,
  signal: AbortSignal,
): Promise<{
  elements: LightweightElement[];
  areaDataSets: AreaDataSets[];
}> {
  const elements = await client.getLightweightProjectStructure({ signal });

  // This should be only one
  const datasets = elements.filter(isLightweightDataSetCapture);

  // This should be only one: there's only one area containing this dataset
  const targetAreas = await Promise.all(
    datasets.map((d) => client.queryAreaVolumeInverse(d.id, signal)),
  );

  const areaDataSets: AreaDataSets[] = [];
  for (let i = 0; i < targetAreas.length; ++i) {
    const dataset = datasets[i];
    for (const area of targetAreas[i]) {
      areaDataSets.push({
        areaId: area.elementId,
        dataSets: [
          {
            elementId: dataset.id,
            elementType: dataset.type,
            elementTypeHint: dataset.typeHint,
            pose: area.pose,
          },
        ],
      });
    }
  }
  return { elements, areaDataSets };
}
